import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Collapse,
  IconButton,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const BasicInformation = ({
  formData,
  handleChange,
  expanded,
  toggleSection
}) => {
  return (
    <Card
      elevation={3}
      sx={{
        mb: 3,
        borderLeft: '6px solid #3f51b5',
        borderRadius: 2,
        backgroundColor: '#fafafa'
      }}
    >
      <CardHeader
        avatar={<InfoIcon sx={{ color: '#3f51b5' }} />}
        title={
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>
            Basic Information
          </Typography>
        }
        action={
          <IconButton onClick={() => toggleSection('basicInfo')}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
        sx={{
          backgroundColor: '#e8eaf6',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          py: 1.5,
          px: 2
        }}
      />
      <Divider />
      <Collapse in={expanded}>
        <CardContent sx={{ px: 3, py: 2 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Product Type</InputLabel>
                <Select
                  name="product_type"
                  value={formData.product_type || 'event'}
                  onChange={handleChange}
                  label="Product Type"
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    '& fieldset': {
                      borderColor: '#9fa8da'
                    },
                    '&:hover fieldset': {
                      borderColor: '#7986cb'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3f51b5'
                    }
                  }}
                >
                  <MenuItem value="event">🎫 Event</MenuItem>
                  <MenuItem value="attendees">👥 Attendees</MenuItem>
                  <MenuItem value="credit">💳 Credit Plan</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.product_type === 'credit' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Credits"
                  variant="outlined"
                  type="number"
                  name="credits"
                  value={formData.credits || ''}
                  onChange={handleChange}
                  placeholder="Number of credits"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      backgroundColor: '#fff',
                      '& fieldset': {
                        borderColor: '#9fa8da'
                      },
                      '&:hover fieldset': {
                        borderColor: '#7986cb'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3f51b5'
                      }
                    }
                  }}
                />
              </Grid>
            )}

            {formData.product_type === 'credit' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Tier</InputLabel>
                    <Select
                      name="tier"
                      value={formData.tier || ''}
                      onChange={handleChange}
                      label="Tier"
                      sx={{
                        backgroundColor: '#fff',
                        borderRadius: 1,
                        '& fieldset': {
                          borderColor: '#9fa8da'
                        },
                        '&:hover fieldset': {
                          borderColor: '#7986cb'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3f51b5'
                        }
                      }}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Starter">Starter</MenuItem>
                      <MenuItem value="Growth">Growth</MenuItem>
                      <MenuItem value="Scale">Scale</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Tier Level</InputLabel>
                    <Select
                      name="tier_level"
                      value={formData.tier_level || ''}
                      onChange={handleChange}
                      label="Tier Level"
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value={1}>Level 1</MenuItem>
                      <MenuItem value={2}>Level 2</MenuItem>
                      <MenuItem value={3}>Level 3</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Badge</InputLabel>
                    <Select
                      name="badge"
                      value={formData.badge || ''}
                      onChange={handleChange}
                      label="Badge"
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Most Popular">Most Popular</MenuItem>
                      <MenuItem value="Best Value">Best Value</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tier Description (Dynamic)"
                    name="tier_description"
                    value={formData.tier_description || ''}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="e.g. For teams actively building pipelines"
                    helperText="This description will apply to the entire tier."
                  />
                </Grid>
              </>
            )}

            {[
              {
                label: formData.product_type === 'attendees' ? 'Attendee Name' : (formData.product_type === 'credit' ? 'Product Name' : 'Event Name'),
                name: 'event_name'
              },
              { label: 'Country', name: 'country' },
              { label: 'Event Date Text', name: 'events_date', placeholder: '25th Aug - 28th Aug 2026' },
              { label: 'Event Date Start', name: 'events_date_start', type: 'date' },
              { label: 'Event Date End', name: 'events_date_end', type: 'date' },
              { label: 'Price', name: 'price', type: 'number' },
              { label: 'Slug', name: 'slug', placeholder: 'iwf-atlanta-exhibitor-list' },
              { label: 'Location', name: 'location', placeholder: 'GWCC Atlanta, Georgia, USA' },
              { label: 'Industries', name: 'industries', placeholder: 'woodworking industry' },
              { label: 'Total Exhibitors', name: 'total_exhibitor', type: 'number', placeholder: '556' },
              { label: 'WooCommerce Product ID', name: 'woocommerce_product_id', placeholder: 'WooCommerce Product ID' }
            ].filter(f => {
              if (formData.product_type === 'credit') {
                return ['event_name', 'price', 'slug'].includes(f.name);
              }
              return true;
            }).map(({ label, name, type = 'text', placeholder = '' }) => (
              <Grid key={name} item xs={12} sm={6}>
                <TextField
                  label={label}
                  variant="outlined"
                  type={type}
                  name={name}
                  value={formData[name] || ''}
                  onChange={handleChange}
                  placeholder={placeholder}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      backgroundColor: '#fff',
                      '& fieldset': {
                        borderColor: '#9fa8da'
                      },
                      '&:hover fieldset': {
                        borderColor: '#7986cb'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3f51b5'
                      }
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Collapse>
    </Card >
  );
};

export default BasicInformation;
